import type { AIContext, AIContextOverrides, ToneOverride } from '../types';
import { formOptions } from '@tanstack/react-form';
import { z } from 'zod';
import { CUSTOM_INDUSTRY_OPTION_VALUE, toFormData, type FormData } from '@/features/business-profile-forms';

export type { FormData };

export type ToneDimensionKey = keyof ToneOverride;

/**
 * Converts between backend and UI scale for tone dimensions.
 * Backend stores formality/respect inverted vs UI: Formal=1, Casual=5 (formality).
 * UI shows Casual=1 (left), Formal=5 (right). Same formula works both directions (6 - score).
 */
export function getToneDisplayScore(key: ToneDimensionKey, score: number): number {
  if (key === 'formality' || key === 'respect') {
    return Math.max(1, Math.min(5, 6 - score));
  }
  return score;
}

export const MAX_TEXT_AREA_LENGTH = 1000;
export const MAX_TEXT_FIELD_LENGTH = 300;

export const formDataValidationSchema = z.object({
  name: z.string().max(MAX_TEXT_FIELD_LENGTH, `Business name must be at most ${MAX_TEXT_FIELD_LENGTH} characters`),
  websiteUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://example.com)'),
  ]),
  industry: z.string().max(MAX_TEXT_FIELD_LENGTH, `Industry must be at most ${MAX_TEXT_FIELD_LENGTH} characters`),
  customIndustry: z.string().max(MAX_TEXT_FIELD_LENGTH, `Industry must be at most ${MAX_TEXT_FIELD_LENGTH} characters`),
  longDescription: z
    .string()
    .max(MAX_TEXT_AREA_LENGTH, `Description must be at most ${MAX_TEXT_AREA_LENGTH} characters`),
  audience: z
    .string()
    .max(MAX_TEXT_AREA_LENGTH, `Audience description must be at most ${MAX_TEXT_AREA_LENGTH} characters`),
  formalityScore: z.number().min(1).max(5),
  humorScore: z.number().min(1).max(5),
  respectScore: z.number().min(1).max(5),
  energyScore: z.number().min(1).max(5),
  privacyPolicyUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://example.com)'),
  ]),
  termsOfServiceUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://example.com/terms-of-service)'),
  ]),
  pricingUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://example.com/pricing)'),
  ]),
  downloadUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://example.com/download)'),
  ]),
  iosUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://apps.apple.com/...)'),
  ]),
  androidUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://play.google.com/...)'),
  ]),
  macUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://apps.apple.com/...)'),
  ]),
  windowsUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://apps.microsoft.com/...)'),
  ]),
  webUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://example.com)'),
  ]),
  browserExtensionUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://chromewebstore.google.com/...)'),
  ]),
  apiUrl: z.union([
    z.literal(''), // Allow empty string
    z.string().url('Please enter a valid URL (e.g., https://api.example.com)'),
  ]),
});

export const aiBusinessProfileFormOptions = formOptions({
  defaultValues: {
    name: '',
    websiteUrl: '',
    industry: '',
    customIndustry: '',
    longDescription: '',
    audience: '',
    formalityScore: 5,
    humorScore: 1,
    respectScore: 5,
    energyScore: 1,
    privacyPolicyUrl: '',
    termsOfServiceUrl: '',
    pricingUrl: '',
    downloadUrl: '',
    iosUrl: '',
    androidUrl: '',
    macUrl: '',
    windowsUrl: '',
    webUrl: '',
    browserExtensionUrl: '',
    apiUrl: '',
  },
  validators: {
    onChange: formDataValidationSchema,
  },
});

export { toFormData };

export const toAIContextOverrides = (formData?: FormData): AIContextOverrides => {
  const {
    name,
    websiteUrl,
    industry,
    customIndustry,
    longDescription,
    audience,
    formalityScore,
    humorScore,
    respectScore,
    energyScore,
    privacyPolicyUrl,
    termsOfServiceUrl,
    pricingUrl,
    downloadUrl,
    iosUrl,
    androidUrl,
    macUrl,
    windowsUrl,
    webUrl,
    browserExtensionUrl,
    apiUrl,
  } = formData ?? {};

  // Reverse formality and respect scores to maintain semantic meaning with backend (same scale conversion as getToneDisplayScore)
  const reversedFormalityScore =
    formalityScore !== undefined ? getToneDisplayScore('formality', formalityScore) : undefined;
  const reversedRespectScore = respectScore !== undefined ? getToneDisplayScore('respect', respectScore) : undefined;

  return {
    name,
    website_url: websiteUrl,
    // If industry is set to CUSTOM_INDUSTRY_OPTION_VALUE, use the value from the customIndustry field; otherwise, use the value from industry
    industry: industry === CUSTOM_INDUSTRY_OPTION_VALUE ? customIndustry : industry,
    long_description: longDescription,
    audience,
    tone: {
      formality: reversedFormalityScore !== undefined ? { score: reversedFormalityScore, description: '' } : undefined,
      humor: humorScore !== undefined ? { score: humorScore, description: '' } : undefined,
      respect: reversedRespectScore !== undefined ? { score: reversedRespectScore, description: '' } : undefined,
      energy: energyScore !== undefined ? { score: energyScore, description: '' } : undefined,
    },
    privacy_policy_url: privacyPolicyUrl,
    terms_of_service_url: termsOfServiceUrl,
    pricing_url: pricingUrl,
    download_url: downloadUrl,
    platforms: {
      ios: { link: iosUrl, available: Boolean(iosUrl) },
      android: { link: androidUrl, available: Boolean(androidUrl) },
      mac: { link: macUrl, available: Boolean(macUrl) },
      windows: { link: windowsUrl, available: Boolean(windowsUrl) },
      web: { link: webUrl, available: Boolean(webUrl) },
      browserExtension: { link: browserExtensionUrl, available: Boolean(browserExtensionUrl) },
      api: { link: apiUrl, available: Boolean(apiUrl) },
    },
  };
};

// Mapping of FormData keys to their source value getters (works for both AIContext and AIContextOverrides)
const FIELD_SOURCE_GETTERS: Record<keyof FormData, ((data?: AIContext | AIContextOverrides) => unknown) | null> = {
  name: (d) => d?.name,
  websiteUrl: (d) => d?.website_url,
  industry: (d) => d?.industry,
  customIndustry: null, // Not counted separately - industry already represents this
  longDescription: (d) => d?.long_description,
  audience: (d) => d?.audience,
  formalityScore: (d) => d?.tone?.formality?.score,
  humorScore: (d) => d?.tone?.humor?.score,
  respectScore: (d) => d?.tone?.respect?.score,
  energyScore: (d) => d?.tone?.energy?.score,
  privacyPolicyUrl: (d) => d?.privacy_policy_url,
  termsOfServiceUrl: (d) => d?.terms_of_service_url,
  pricingUrl: (d) => d?.pricing_url,
  downloadUrl: (d) => d?.download_url,
  iosUrl: (d) => d?.platforms?.ios?.link,
  androidUrl: (d) => d?.platforms?.android?.link,
  macUrl: (d) => d?.platforms?.mac?.link,
  windowsUrl: (d) => d?.platforms?.windows?.link,
  webUrl: (d) => d?.platforms?.web?.link,
  browserExtensionUrl: (d) => d?.platforms?.browserExtension?.link,
  apiUrl: (d) => d?.platforms?.api?.link,
};

export function countFieldStats(aiContext?: AIContext, overrides?: AIContextOverrides) {
  const formData = toFormData(aiContext, overrides);
  let total = 0,
    aiPopulated = 0,
    userOverridden = 0,
    empty = 0;

  const hasValue = (value: unknown): boolean => value != null && value !== '';

  for (const [key, value] of Object.entries(formData)) {
    const getter = FIELD_SOURCE_GETTERS[key as keyof FormData];

    if (getter === null) {
      continue;
    }

    const fieldHasValue = hasValue(value);

    if (!fieldHasValue) {
      empty++;
    } else {
      const isFromOverride = hasValue(getter(overrides));
      const isFromAIContext = !isFromOverride && hasValue(getter(aiContext));

      if (isFromOverride) {
        userOverridden++;
      } else if (isFromAIContext) {
        aiPopulated++;
      }
    }

    total++;
  }

  return { total, aiPopulated, userOverridden, empty };
}
